import React, { useState, useEffect } from 'react';
import { AlertCircle, Archive, Download } from 'lucide-react';
import { auditArchive } from '../../services/auditArchive';

export function AuditStorageHealth() {
  const [capacity, setCapacity] = useState(null);
  const [archives, setArchives] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const checkStorage = () => {
      try {
        const cap = auditArchive.checkCapacity();
        const arcs = auditArchive.getArchives();

        setCapacity(cap);
        setArchives(arcs);
      } catch (error) {
        console.error('Failed to check audit capacity:', error);
      }
    };

    checkStorage();

    // Check every minute
    const interval = setInterval(checkStorage, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleExport = () => {
    setIsExporting(true);
    try {
      const data = auditArchive.exportAll('json');
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-complete-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export audit log');
    } finally {
      setIsExporting(false);
    }
  };

  const handleArchiveNow = () => {
    try {
      auditArchive.archive();
      // Refresh capacity
      const cap = auditArchive.checkCapacity();
      const arcs = auditArchive.getArchives();
      setCapacity(cap);
      setArchives(arcs);
    } catch (error) {
      console.error('Archive failed:', error);
      alert('Failed to archive audit log');
    }
  };

  if (!capacity) {
    return null;
  }

  // Only show if warning or critical
  if (capacity.status === 'normal') {
    return null;
  }

  const isCritical = capacity.status === 'critical';

  return (
    <div className={`border-l-4 p-4 mb-4 rounded-r ${
      isCritical
        ? 'bg-red-50 border-red-500'
        : 'bg-yellow-50 border-yellow-500'
    }`}>
      <div className="flex items-start gap-3">
        <AlertCircle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
          isCritical ? 'text-red-600' : 'text-yellow-600'
        }`} />

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">
            {isCritical
              ? 'Audit Log Critical - Archival Required'
              : 'Audit Log Storage Warning'}
          </h3>

          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium">{capacity.current}</span> of{' '}
              <span className="font-medium">{capacity.max}</span> events{' '}
              ({Math.round(capacity.percentage)}% capacity)
            </p>

            {archives.length > 0 && (
              <p className="text-sm text-gray-600">
                {archives.length} archive{archives.length !== 1 ? 's' : ''} available
              </p>
            )}
          </div>

          <p className="text-sm text-gray-600 mt-3">
            {isCritical
              ? 'Oldest events will be archived automatically. Export now to preserve all data for MCA inspection.'
              : 'Approaching audit log capacity. Consider exporting logs to preserve historical data.'}
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Exporting...' : `Export All Events (${capacity.current})`}
            </button>

            {isCritical && (
              <button
                onClick={handleArchiveNow}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Archive className="h-4 w-4" />
                Archive Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
